import { useLayoutEffect, useRef, useState } from 'react';
import type { MetricPoint } from '@lizrd/contracts';

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  points: MetricPoint[];
}

const PAD = { top: 10, right: 14, bottom: 22, left: 52 };

/** Round a max up to a clean gridline ceiling (1/2/5 × 10ⁿ). */
function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const f = n / pow;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * pow;
}

const shortDay = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}`;
};

/**
 * Build the shared day axis + per-series day→value lookups. The axis is the sorted
 * union of every series' days (ISO strings sort chronologically), so each series is
 * positioned by its actual date rather than its array index — a shorter or gappy
 * series would otherwise render (and read on hover) against the wrong dates.
 */
export function dayAxis(series: LineSeries[]): {
  days: string[];
  valueByDay: Map<string, number>[];
} {
  const days = Array.from(new Set(series.flatMap((s) => s.points.map((p) => p.day)))).sort();
  const valueByDay = series.map((s) => new Map(s.points.map((p) => [p.day, p.value])));
  return { days, valueByDay };
}

/**
 * A zero-dependency multi-line chart (inline SVG, theme-tokened) for daily cost
 * series that share one ordered day axis. Y starts at 0 (honest for money),
 * gridlines + value labels on the left, dates on the bottom, and a hover
 * crosshair with a per-series tooltip. Responsive via a measured width.
 */
export function LineChart({
  series,
  height = 210,
  yMax,
  yTicks,
  formatValue = (n) => `$${Math.round(n).toLocaleString()}`,
  formatTick,
}: {
  series: LineSeries[];
  height?: number;
  /** Fixed y-axis maximum (e.g. a 0–5 score); defaults to a nice ceiling of the data. */
  yMax?: number;
  /** Explicit y-axis gridline/label values (e.g. [1,2,3,4,5]); defaults to quarters of the max. */
  yTicks?: number[];
  formatValue?: (n: number) => string;
  /** Formats the y-axis tick labels; defaults to {@link formatValue}. */
  formatTick?: (n: number) => string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setWidth(e[0]!.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  // Shared day axis (sorted union of all series' days) + per-series day→value maps, so
  // both the line and the hover read the value for the axis's day, not a shared array
  // index — see {@link dayAxis}.
  const { days, valueByDay } = dayAxis(series);
  const n = days.length;
  const dayIndex = new Map(days.map((d, i) => [d, i]));
  const rawMax = Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.value)));
  const top = yMax ?? niceCeil(rawMax);

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;

  const ticks = yTicks ?? [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  const tickLabel = formatTick ?? formatValue;

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 && n > 0 && (
        <svg
          width={width}
          height={height}
          onPointerMove={(e) => {
            const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
            const rel = e.clientX - rect.left - PAD.left;
            const idx = Math.round((rel / plotW) * (n - 1));
            setHover(Math.max(0, Math.min(n - 1, idx)));
          }}
          onPointerLeave={() => setHover(null)}
        >
          {/* Gridlines + y labels */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--hairline)"
                strokeWidth={1}
                opacity={0.5}
              />
              <text
                x={PAD.left - 6}
                y={y(t) + 3}
                textAnchor="end"
                className="fill-muted"
                style={{ fontSize: 9 }}
              >
                {tickLabel(t)}
              </text>
            </g>
          ))}

          {/* X labels (first / mid / last) */}
          {n > 0 &&
            [0, Math.floor((n - 1) / 2), n - 1]
              .filter((v, i, a) => a.indexOf(v) === i)
              .map((i) => (
                <text
                  key={i}
                  x={x(i)}
                  y={height - 6}
                  textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
                  className="fill-muted"
                  style={{ fontSize: 9 }}
                >
                  {shortDay(days[i]!)}
                </text>
              ))}

          {/* Series lines — each point placed at its own day's x, not its array index */}
          {series.map((s) => (
            <polyline
              key={s.key}
              points={[...s.points]
                .filter((p) => dayIndex.has(p.day))
                .sort((a, b) => a.day.localeCompare(b.day))
                .map((p) => `${x(dayIndex.get(p.day)!)},${y(p.value)}`)
                .join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Hover crosshair + dots */}
          {hover != null && (
            <>
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.top}
                y2={PAD.top + plotH}
                stroke="var(--border-strong)"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              {series.map((s, si) => {
                const v = valueByDay[si]!.get(days[hover]!);
                if (v == null) return null; // this series has no point on the hovered day
                return <circle key={s.key} cx={x(hover)} cy={y(v)} r={3} fill={s.color} />;
              })}
            </>
          )}
        </svg>
      )}

      {/* Tooltip */}
      {hover != null && width > 0 && (
        <div
          className="pointer-events-none absolute z-10 min-w-[7rem] rounded-md border border-hairline bg-surface-2 p-2 text-[0.7rem] shadow-card"
          style={{
            left: Math.min(Math.max(x(hover) + 8, 4), width - 130),
            top: PAD.top,
          }}
        >
          <div className="mb-1 font-semibold text-bone">{shortDay(days[hover]!)}</div>
          {series.map((s, si) => {
            const v = valueByDay[si]!.get(days[hover]!);
            return (
              <div key={s.key} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-muted">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.label}
                </span>
                <span className="tnum text-bone">{v == null ? '—' : formatValue(v)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
